import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Navigate } from 'react-router-dom';
import Loader from '../../components/Loader';
import useCurrentUser from '../../hooks/useCurrentUser';
import { createUser, deleteUser, fetchAccounts, fetchUser, fetchUsers, resetUserPassword, updateUser } from '../../services/userService';

const PageContainer = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
`;

const PrimaryButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background: #0056b3;
  }
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const SmallButton = styled.button`
  background: ${(props) => (props.danger ? '#dc3545' : props.secondary ? '#6c757d' : '#007bff')};
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
  white-space: nowrap;
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
`;

const Th = styled.th`
  background: #002d62;
  color: white;
  padding: 10px;
  text-align: left;
  font-size: 13px;
`;

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  vertical-align: middle;
  color: ${(props) => (props.muted ? '#999' : 'inherit')};
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 1px 4px 1px 0;
  border-radius: 10px;
  font-size: 11px;
  background: ${(props) => (props.kind === 'admin' ? '#dc3545' : props.kind === 'group' ? '#17a2b8' : '#e9ecef')};
  color: ${(props) => (props.kind === 'admin' || props.kind === 'group' ? 'white' : '#333')};
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  background: ${(props) => (props.enabled ? '#28a745' : '#dc3545')};
`;

const Message = styled.div`
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  background: ${(props) => (props.error ? '#f8d7da' : '#d4edda')};
  color: ${(props) => (props.error ? '#721c24' : '#155724')};
`;

const ModalBackground = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 5px;
  width: 640px;
  max-width: 92%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 13px;
`;

const Hint = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 3px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-sizing: border-box;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 6px 0;
  font-size: 13px;
  cursor: pointer;
  input {
    margin-top: 2px;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const MenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const MenuList = styled.div`
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  min-width: 160px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 50;
  padding: 4px 0;
`;

const MenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  color: ${(props) => (props.danger ? '#dc3545' : '#333')};
  &:hover:not(:disabled) {
    background: #f1f3f5;
  }
  &:disabled {
    color: #aaa;
    cursor: not-allowed;
  }
`;

/** Per-row "Actions ▾" dropdown; closes on outside click or Escape. */
const ActionsMenu = ({ items, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <MenuWrapper ref={ref}>
      <SmallButton secondary onClick={() => setOpen((o) => !o)} disabled={disabled} aria-haspopup="menu" aria-expanded={open}>
        Actions ▾
      </SmallButton>
      {open && (
        <MenuList role="menu">
          {items.map((item) => (
            <MenuItem
              key={item.label}
              role="menuitem"
              danger={item.danger}
              disabled={item.disabled}
              title={item.title}
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
            >
              {item.label}
            </MenuItem>
          ))}
        </MenuList>
      )}
    </MenuWrapper>
  );
};

/** "3 minutes ago" / "2 days ago" style rendering for the Last active column. */
const relativeTime = (iso) => {
  if (!iso) return null;
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
};

const Select = styled.select`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background: white;
`;

const ROLES = [
  { value: 'supervisor', label: 'Supervisor — everything in their account, full control' },
  { value: 'manager', label: 'Manager — full control within one customer number' },
  { value: 'employee', label: 'Employee — sees their customer number, no Customers page' },
];

const roleLabel = (r) => ({ supervisor: 'Supervisor', manager: 'Manager', employee: 'Employee' }[r] || r || '');

const emptyForm = { username: '', email: '', accountId: '', role: '', custNo: '', admin: false, enabled: true };

const toForm = (u) => ({
  username: u.username,
  email: u.email || '',
  accountId: u.accountId || '',
  role: u.role || '',
  custNo: u.custNo || '',
  admin: (u.groups || []).includes('admin'),
  enabled: u.enabled,
});

const UserModal = ({ mode, initial, accounts, currentUsername, onClose, onSave }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isSelf = mode === 'edit' && initial.username === currentUsername;

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const account = accounts.find((a) => a.id === form.accountId);
  const numbers = useMemo(() => account?.customerNumbers || [], [account]);
  const needsNumber = form.role === 'manager' || form.role === 'employee';

  // an account with a single customer number needs no choice — inherit it
  useEffect(() => {
    if (needsNumber && numbers.length === 1 && form.custNo !== numbers[0]) {
      setForm((f) => ({ ...f, custNo: numbers[0] }));
    }
  }, [needsNumber, numbers, form.custNo]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave({
        username: form.username.trim(),
        email: form.email.trim(),
        accountId: form.accountId || null,
        role: form.accountId ? form.role || null : null,
        custNo: form.accountId && needsNumber ? form.custNo || null : null,
        // only send groups when the admin flag actually changed, so a save from a
        // stale view can never silently strip a group added elsewhere
        groups: mode === 'edit' && form.admin === initial.admin ? undefined : form.admin ? ['admin'] : [],
        enabled: form.enabled,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBackground>
      <ModalContent>
        <h3 style={{ marginTop: 0 }}>{mode === 'create' ? 'New user' : `Edit ${initial.username}`}</h3>
        {error && <Message error>{error}</Message>}
        <form onSubmit={submit}>
          <FormGroup>
            <Label>Username</Label>
            <Input value={form.username} onChange={set('username')} disabled={mode === 'edit'} required autoFocus={mode === 'create'} />
          </FormGroup>
          <FormGroup>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={set('email')} required />
            {mode === 'create' && <Hint>A temporary password will be emailed to this address.</Hint>}
          </FormGroup>
          <FormGroup>
            <Label>Account</Label>
            <Select value={form.accountId} onChange={(e) => setForm({ ...form, accountId: e.target.value, custNo: '' })} disabled={form.admin}>
              <option value="">— none —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            {form.admin ? <Hint>Admins see every account; no assignment needed.</Hint> : <Hint>A user without an account sees nothing.</Hint>}
          </FormGroup>
          {form.accountId && !form.admin && (
            <FormGroup>
              <Label>Role</Label>
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required>
                <option value="">— choose a role —</option>
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </FormGroup>
          )}
          {form.accountId && !form.admin && needsNumber && numbers.length > 1 && (
            <FormGroup>
              <Label>Customer number</Label>
              <Select value={form.custNo} onChange={set('custNo')} required>
                <option value="">— choose —</option>
                {numbers.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </Select>
              <Hint>{account.name} owns several customer numbers; a {roleLabel(form.role).toLowerCase()} is pinned to one.</Hint>
            </FormGroup>
          )}
          {form.accountId && !form.admin && needsNumber && numbers.length === 1 && (
            <FormGroup>
              <Label>Customer number</Label>
              <Hint style={{ fontSize: '13px', color: '#333' }}>
                {numbers[0]} — inherited from {account.name}
              </Hint>
            </FormGroup>
          )}
          {form.accountId && !form.admin && needsNumber && numbers.length === 0 && (
            <FormGroup>
              <Label>Customer number</Label>
              <Hint>This account has no customer numbers yet — add them on the Accounts page first.</Hint>
            </FormGroup>
          )}
          <FormGroup>
            <CheckboxRow>
              <input
                type="checkbox"
                checked={form.admin}
                onChange={(e) => setForm({ ...form, admin: e.target.checked })}
                disabled={isSelf}
              />
              <span>
                <strong>Admin</strong> — manages accounts and users, sees every company
              </span>
            </CheckboxRow>
          </FormGroup>
          {mode === 'edit' && (
            <FormGroup>
              <CheckboxRow>
                <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} disabled={isSelf} />
                <span>Account enabled</span>
              </CheckboxRow>
              {isSelf && <Hint>You can't disable your own account or remove your own admin permission.</Hint>}
            </FormGroup>
          )}
          <ModalButtons>
            <SmallButton type="button" secondary onClick={onClose} disabled={saving}>
              Cancel
            </SmallButton>
            <PrimaryButton type="submit" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Create user' : 'Save changes'}
            </PrimaryButton>
          </ModalButtons>
        </form>
      </ModalContent>
    </ModalBackground>
  );
};

const UsersPage = () => {
  const { user: me, loading: meLoading, isAdmin } = useCurrentUser();
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [modal, setModal] = useState(null); // { mode: 'create' } | { mode: 'edit', user }
  const [busy, setBusy] = useState(null); // username of a row with an action in flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, a] = await Promise.all([fetchUsers(), fetchAccounts()]);
      setUsers(u);
      setAccounts(a);
      setMessage(null);
    } catch (err) {
      setMessage({ error: true, text: `Could not load users: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const notify = (text) => setMessage({ error: false, text });
  const fail = (err) => setMessage({ error: true, text: err.message });

  const withBusy = async (username, fn) => {
    setBusy(username);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  // fetch the user fresh so the edit form never starts from a stale row
  const openEdit = async (u) => {
    setBusy(u.username);
    try {
      const fresh = await fetchUser(u.username);
      setUsers((prev) => prev.map((x) => (x.username === fresh.username ? fresh : x)));
      setModal({ mode: 'edit', user: fresh });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const handleSave = async (values) => {
    if (modal.mode === 'create') {
      const created = await createUser(values);
      setUsers((prev) => [...prev, created].sort((a, b) => a.username.localeCompare(b.username)));
      notify(`Created ${created.username}; a temporary password was emailed to ${created.email}.`);
    } else {
      const { username, ...changes } = values;
      const updated = await updateUser(username, changes);
      setUsers((prev) => prev.map((u) => (u.username === username ? updated : u)));
      notify(`Saved ${username}.${username === me.username ? ' Sign out and back in for your own changes to take effect.' : ''}`);
    }
  };

  const handleToggleEnabled = (u) =>
    withBusy(u.username, async () => {
      const updated = await updateUser(u.username, { enabled: !u.enabled });
      setUsers((prev) => prev.map((x) => (x.username === u.username ? updated : x)));
      notify(`${u.username} ${updated.enabled ? 'enabled' : 'disabled and signed out'}.`);
    });

  const handleReset = (u) => {
    if (!window.confirm(`Email a new temporary password to ${u.email}?`)) return;
    withBusy(u.username, async () => {
      await resetUserPassword(u.username);
      notify(`Password reset email sent to ${u.email}.`);
    });
  };

  const handleDelete = (u) => {
    if (!window.confirm(`Permanently delete the login "${u.username}"? This cannot be undone.`)) return;
    withBusy(u.username, async () => {
      await deleteUser(u.username);
      setUsers((prev) => prev.filter((x) => x.username !== u.username));
      notify(`Deleted ${u.username}.`);
    });
  };

  const accountName = (id) => accounts.find((a) => a.id === id)?.name;

  if (meLoading) return <Loader />;
  if (!isAdmin) return <Navigate to="/insights" replace />;

  return (
    <PageContainer>
      <Header>
        <Title>Users</Title>
        <div>
          <PrimaryButton onClick={() => setModal({ mode: 'create' })}>New user</PrimaryButton>
        </div>
      </Header>
      {message && <Message error={message.error}>{message.text}</Message>}
      {loading ? (
        <Loader />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>USERNAME</Th>
              <Th>EMAIL</Th>
              <Th>ACCOUNT</Th>
              <Th>ROLE</Th>
              <Th>STATUS</Th>
              <Th>LAST ACTIVE</Th>
              <Th>ACTIONS</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.username === me.username;
              return (
                <tr key={u.username}>
                  <Td>
                    <strong>{u.username}</strong>
                    {isSelf && <Chip> you</Chip>}
                  </Td>
                  <Td>{u.email}</Td>
                  <Td muted={!u.groups.includes('admin') && !u.accountId}>
                    {u.groups.includes('admin') ? 'all (admin)' : accountName(u.accountId) || 'none'}
                  </Td>
                  <Td muted={!u.groups.includes('admin') && !u.role}>
                    {u.groups.includes('admin') ? (
                      <Chip kind="admin">admin</Chip>
                    ) : u.role ? (
                      <>
                        <Chip kind="group">{roleLabel(u.role)}</Chip>
                        {u.custNo && <Chip>{u.custNo}</Chip>}
                      </>
                    ) : (
                      'none'
                    )}
                  </Td>
                  <Td>
                    <StatusDot enabled={u.enabled} />
                    {u.enabled ? 'Enabled' : 'Disabled'}
                    {u.status === 'FORCE_CHANGE_PASSWORD' && <Hint>Hasn't signed in yet</Hint>}
                  </Td>
                  <Td muted={!u.lastSeen} title={u.lastSeen ? new Date(u.lastSeen).toLocaleString() : ''}>
                    {relativeTime(u.lastSeen) || 'never'}
                  </Td>
                  <Td>
                    <ActionsMenu
                      disabled={busy === u.username}
                      items={[
                        { label: 'Edit', onClick: () => openEdit(u) },
                        { label: 'Reset password', onClick: () => handleReset(u) },
                        {
                          label: u.enabled ? 'Disable' : 'Enable',
                          onClick: () => handleToggleEnabled(u),
                          disabled: isSelf,
                          title: isSelf ? "You can't disable your own account" : undefined,
                        },
                        {
                          label: 'Delete',
                          onClick: () => handleDelete(u),
                          danger: true,
                          disabled: isSelf,
                          title: isSelf ? "You can't delete your own account" : undefined,
                        },
                      ]}
                    />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
      {modal && (
        <UserModal
          mode={modal.mode}
          initial={modal.mode === 'create' ? emptyForm : toForm(modal.user)}
          accounts={accounts}
          currentUsername={me.username}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </PageContainer>
  );
};

export default UsersPage;
